import React, { useEffect, useRef, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScanFace, Camera, CheckCircle2, AlertTriangle, Loader2, Eye, RefreshCw } from "lucide-react";
import { loadFaceModels, detectFace, getDescriptor, bestDistance, MATCH_THRESHOLD, EAR_OPEN, EAR_CLOSED, TURN_CENTER, TURN_ON } from "@/lib/faceModels";

const SAMPLES_NEEDED = 3;

export default function FaceScanDialog({
  open, onOpenChange, mode = "verify", stored = [], graceSeconds = 0,
  title, description, onRegistered, onVerified, onFail,
}) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const runningRef = useRef(false);
  const doneRef = useRef(false);
  // liveness challenge state
  const challengeRef = useRef("blink");
  const sawOpenRef = useRef(false);
  const blinkedRef = useRef(false);
  const sawCenterRef = useRef(false);
  const turnedRef = useRef(false);
  const [phase, setPhase] = useState("loading"); // loading | ready | matched | error
  const [hint, setHint] = useState("");
  const [challenge, setChallenge] = useState("blink");
  const [live, setLive] = useState(false);
  const [samples, setSamples] = useState([]);
  const [busy, setBusy] = useState(false);
  const [countdown, setCountdown] = useState(graceSeconds);

  const stopCamera = useCallback(() => {
    runningRef.current = false;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const captureSnapshot = () => {
    try {
      const v = videoRef.current;
      if (!v || v.readyState < 2) return null;
      const canvas = document.createElement("canvas");
      canvas.width = v.videoWidth || 320;
      canvas.height = v.videoHeight || 240;
      canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL("image/jpeg", 0.7);
    } catch { return null; }
  };

  const handleOpenChange = useCallback((o) => {
    if (o) return;
    if (graceSeconds > 0 && !doneRef.current) {
      doneRef.current = true;
      const snap = captureSnapshot();
      stopCamera();
      onFail && onFail(snap);
      onOpenChange(false);
      return;
    }
    stopCamera();
    onOpenChange(false);
  }, [graceSeconds, stopCamera, onFail, onOpenChange]);

  const startVerifyLoop = useCallback(() => {
    runningRef.current = true;
    const tick = async () => {
      if (!runningRef.current || doneRef.current) return;
      try {
        const res = await detectFace(videoRef.current);
        if (res) {
          const { descriptor, ear, yaw } = res;
          // blink liveness
          if (ear > EAR_OPEN) sawOpenRef.current = true;
          if (sawOpenRef.current && ear < EAR_CLOSED) blinkedRef.current = true;
          // head-turn liveness
          if (Math.abs(yaw) < TURN_CENTER) sawCenterRef.current = true;
          if (sawCenterRef.current && Math.abs(yaw) > TURN_ON) turnedRef.current = true;

          const isTurn = challengeRef.current === "turn";
          const liveOk = isTurn ? turnedRef.current : blinkedRef.current;
          if (liveOk) setLive(true);
          const matched = bestDistance(descriptor, stored) <= MATCH_THRESHOLD;

          if (matched && liveOk) {
            doneRef.current = true;
            runningRef.current = false;
            setPhase("matched");
            setHint("Verified!");
            setTimeout(() => { stopCamera(); onVerified && onVerified(); onOpenChange(false); }, 600);
            return;
          }
          if (matched && !liveOk) setHint(isTurn ? "Recognised — now slowly turn your head to confirm you're live." : "Recognised — now blink once to confirm you're live.");
          else if (!matched && liveOk) setHint("Liveness OK — hold still while we recognise you…");
          else setHint(isTurn ? "Look at the camera, then slowly turn your head left or right." : "Look at the camera and blink once.");
        } else {
          setHint("No face detected — center your face in the frame.");
        }
      } catch (e) { /* transient */ }
      if (runningRef.current && !doneRef.current) setTimeout(tick, 300);
    };
    setTimeout(tick, 500);
  }, [stored, stopCamera, onVerified, onOpenChange]);

  // Start camera + models when the dialog opens.
  useEffect(() => {
    if (!open) return;
    doneRef.current = false;
    sawOpenRef.current = false;
    blinkedRef.current = false;
    sawCenterRef.current = false;
    turnedRef.current = false;
    // Liveness is a head-turn (blink removed at user's request — turning is more reliable).
    const ch = "turn";
    challengeRef.current = ch;
    setChallenge(ch);
    setLive(false);
    setSamples([]);
    setCountdown(graceSeconds);
    setPhase("loading");
    setHint("Loading camera & face models…");
    let cancelled = false;
    (async () => {
      try {
        await loadFaceModels();
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setPhase("ready");
        setHint(mode === "register"
          ? "Center your face, then capture."
          : (ch === "turn" ? "Look at the camera, then slowly turn your head." : "Look at the camera and blink once."));
        if (mode === "verify") startVerifyLoop();
      } catch (e) {
        setPhase("error");
        setHint(e?.name === "NotAllowedError"
          ? "Camera permission denied. Please allow camera access and retry."
          : "No camera available on this device.");
      }
    })();
    return () => { cancelled = true; stopCamera(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Grace countdown for the periodic re-check.
  useEffect(() => {
    if (!open || mode !== "verify" || !graceSeconds || phase !== "ready") return;
    if (countdown <= 0) {
      if (!doneRef.current) { doneRef.current = true; const snap = captureSnapshot(); stopCamera(); onFail && onFail(snap); onOpenChange(false); }
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [open, mode, graceSeconds, phase, countdown, stopCamera, onFail, onOpenChange]);

  const capture = async () => {
    setBusy(true);
    try {
      const desc = await getDescriptor(videoRef.current);
      if (!desc) { setHint("No face detected — center your face and try again."); setBusy(false); return; }
      const next = [...samples, desc];
      setSamples(next);
      if (next.length >= SAMPLES_NEEDED) {
        const photo = captureSnapshot();
        doneRef.current = true;
        stopCamera();
        onRegistered && (await onRegistered(next, photo));
        onOpenChange(false);
      } else {
        setHint(`Captured ${next.length}/${SAMPLES_NEEDED} — turn your head slightly and capture again.`);
      }
    } catch (e) {
      setHint("Something went wrong — please try again.");
    }
    setBusy(false);
  };

  const badge = live ? "Live" : (challenge === "turn" ? "Turn head" : "Blink to verify");

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md" data-testid="face-scan-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-navy flex items-center gap-2">
            <ScanFace className="h-5 w-5 text-gold-dark" /> {title}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500 -mt-2">{description}</p>

        <div className="relative rounded-xl overflow-hidden bg-slate-900 aspect-[4/3] flex items-center justify-center">
          <video ref={videoRef} muted playsInline data-testid="face-video" className="h-full w-full object-cover [transform:scaleX(-1)]" />
          {phase === "loading" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/80">
              <Loader2 className="h-6 w-6 animate-spin" /><span className="text-xs">Starting camera…</span>
            </div>
          )}
          {phase === "error" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4 text-center text-white/90" data-testid="face-error">
              <AlertTriangle className="h-6 w-6 text-amber-400" /><span className="text-xs">{hint}</span>
            </div>
          )}
          {phase === "matched" && (
            <div className="absolute inset-0 flex items-center justify-center bg-emerald-600/70" data-testid="face-matched">
              <CheckCircle2 className="h-14 w-14 text-white" />
            </div>
          )}
          {mode === "verify" && phase === "ready" && (
            <div className={`absolute top-2 left-2 flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${live ? "bg-emerald-500/80 text-white" : "bg-black/60 text-white/80"}`} data-testid="liveness-status">
              {challenge === "turn" ? <RefreshCw className="h-3 w-3" /> : <Eye className="h-3 w-3" />} {badge}
            </div>
          )}
          {mode === "register" && phase === "ready" && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
              {Array.from({ length: SAMPLES_NEEDED }).map((_, i) => (
                <span key={i} className={`h-2.5 w-2.5 rounded-full ${i < samples.length ? "bg-emerald-400" : "bg-white/50"}`} />
              ))}
            </div>
          )}
          {graceSeconds > 0 && phase === "ready" && (
            <div className="absolute top-2 right-2 rounded-full bg-black/60 px-2 py-0.5 text-xs text-white tabular-nums" data-testid="recheck-countdown">{countdown}s</div>
          )}
        </div>

        {phase !== "error" && <p className="text-center text-xs text-slate-500 min-h-[16px]">{hint}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)} data-testid="face-cancel-btn" className="rounded-lg">Cancel</Button>
          {mode === "register" && phase === "ready" && (
            <Button onClick={capture} disabled={busy} data-testid="face-capture-btn" className="bg-navy text-white rounded-lg">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Camera className="mr-2 h-4 w-4" /> Capture {samples.length + 1}/{SAMPLES_NEEDED}</>}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
